import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card } from '@promtver/ui-kit';
import { UserRole } from '@promtver/contracts';

import {
  assignObjectModerator,
  getAdminUsers,
  getEnterprisesForModeration,
  revokeObjectModerator,
  setUserBlocked,
} from '../../api/admin';
import { errorMessage } from '../../api/errors';
import { AppShell } from '../../components/AppShell';
import { AsyncState } from '../../components/AsyncState';
import { KeyValue } from '../../components/KeyValue';
import { Select } from '../../components/Select';
import { ru } from '../../i18n/ru';
import { formatDateTimeLocal } from '../../lib/datetime';

function roleLabel(role: UserRole | string): string {
  switch (role) {
    case UserRole.ObjectModerator:
      return ru.roles.roleObjectModerator;
    case UserRole.RegionAdmin:
      return ru.roles.roleRegionAdmin;
    case UserRole.PlatformOperator:
      return ru.roles.rolePlatformOperator;
    default:
      return ru.roles.roleGuest;
  }
}

/**
 * Управление ролями (FR-ADM-04): назначение модераторов объектов,
 * снятие прав и блокировка учётных записей.
 */
export function RolesPage() {
  const queryClient = useQueryClient();
  const [selectedEnterprise, setSelectedEnterprise] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => getAdminUsers(),
  });

  const enterprisesQuery = useQuery({
    queryKey: ['admin', 'enterprises', 'all-for-roles'],
    queryFn: () => getEnterprisesForModeration(undefined),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  };

  const assignMutation = useMutation({
    mutationFn: ({ userId, enterpriseId }: { userId: string; enterpriseId: string }) =>
      assignObjectModerator(userId, enterpriseId),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError: (cause) => setError(errorMessage(cause, ru.common.unknownError)),
  });

  const revokeMutation = useMutation({
    mutationFn: (userId: string) => revokeObjectModerator(userId),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError: (cause) => setError(errorMessage(cause, ru.common.unknownError)),
  });

  const blockMutation = useMutation({
    mutationFn: ({ userId, blocked }: { userId: string; blocked: boolean }) =>
      setUserBlocked(userId, blocked),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError: (cause) => setError(errorMessage(cause, ru.common.unknownError)),
  });

  const users = usersQuery.data?.items ?? [];
  const enterprises = enterprisesQuery.data?.items ?? [];

  return (
    <AppShell title={ru.roles.title} subtitle={ru.roles.subtitle}>
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}

      <Card title={ru.roles.title}>
        <AsyncState
          isLoading={usersQuery.isLoading}
          isError={usersQuery.isError}
          isEmpty={users.length === 0}
          emptyTitle={ru.roles.empty}
          onRetry={() => void usersQuery.refetch()}
        >
          <ul className="flex flex-col gap-3">
            {users.map((user) => (
              <li key={user.id} className="rounded-card border border-line p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <strong className="text-sm">{user.displayName ?? user.email ?? user.id}</strong>
                  <div className="flex flex-wrap gap-2">
                    <Badge tone="steel">{roleLabel(user.role)}</Badge>
                    {user.isBlocked ? <Badge tone="danger">{ru.roles.blocked}</Badge> : null}
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <KeyValue label={ru.common.email} value={user.email ?? ru.common.notSpecified} mono />
                  <KeyValue label={ru.common.phone} value={user.phone ?? ru.common.notSpecified} mono />
                  <KeyValue label={ru.roles.enterprise} value={user.enterpriseName ?? ru.common.notSpecified} />
                  <KeyValue label={ru.common.created} value={formatDateTimeLocal(user.createdAt)} />
                </div>

                <div className="mt-3 flex flex-col gap-2">
                  <Select
                    label={ru.roles.enterprise}
                    value={selectedEnterprise[user.id] ?? ''}
                    onChange={(value) =>
                      setSelectedEnterprise((previous) => ({ ...previous, [user.id]: value }))
                    }
                    options={[
                      { value: '', label: ru.common.notSpecified },
                      ...enterprises.map((enterprise) => ({
                        value: enterprise.id,
                        label: enterprise.name,
                      })),
                    ]}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      tone="accent"
                      disabled={
                        assignMutation.isPending ||
                        !(selectedEnterprise[user.id] ?? '').trim()
                      }
                      onClick={() =>
                        assignMutation.mutate({
                          userId: user.id,
                          enterpriseId: (selectedEnterprise[user.id] ?? '').trim(),
                        })
                      }
                    >
                      {ru.roles.assignModerator}
                    </Button>
                    <Button
                      variant="outline"
                      disabled={revokeMutation.isPending}
                      onClick={() => revokeMutation.mutate(user.id)}
                    >
                      {ru.roles.revoke}
                    </Button>
                    <Button
                      variant="outline"
                      tone={user.isBlocked ? 'success' : 'danger'}
                      disabled={blockMutation.isPending}
                      onClick={() => blockMutation.mutate({ userId: user.id, blocked: !user.isBlocked })}
                    >
                      {user.isBlocked ? ru.roles.unblock : ru.roles.block}
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </AsyncState>
      </Card>
    </AppShell>
  );
}
